import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { createColumnHelper, RowSelectionState } from '@tanstack/react-table';

import { useApprovals } from 'features/approvals/useApprovals';
import { useManualPayoutApprovals } from 'features/approvals/useManualPayoutApprovals';
import WarningStatusBadge from 'features/users/components/atoms/WarningStatusBadge';
import { Box, Flex, Text } from 'shared/components/display';
import BrkfstTable from 'shared/components/organisms/BrkfstTable';
import ActionButton from 'shared/components/molecules/ActionButton';
import CopyButton from 'shared/components/atoms/CopyButton';
import PromptButton from 'shared/components/molecules/PromptButton';

import { LinkCreator } from 'shared/utilities/linkUtility';
import { prettyDate } from 'shared/utilities/dateUtility';
import { formatCurrency } from 'shared/utilities/stringUtility';

import { useCurrentUser } from 'shared/hooks/useCurrentUser';
import { useComponentLoading } from 'shared/hooks/useComponentLoading';
import { RESOURCES } from 'shared/config/resourceNames';
import { BUTTON_VARIANTS } from 'shared/styles/button';
import { TEXT_VARIANTS } from 'shared/styles/text';
import { ManualPayoutApproval, SubmissionApproval } from 'shared/typings/approvals';

import ApprovalToolbar from './ApprovalToolbar';

const submissionColumnHelper = createColumnHelper<SubmissionApproval>();
const manualColumnHelper = createColumnHelper<ManualPayoutApproval>();

const formatDate = (iso?: string | null) => (iso ? prettyDate(iso) : '—');

const Stamp: React.FC<{ date?: string | null }> = ({ date }) =>
  date ? (
    <Text className="approval-dashboard__stamp approval-dashboard__stamp--done">{formatDate(date)}</Text>
  ) : (
    <Text className="approval-dashboard__stamp approval-dashboard__stamp--pending">Pending</Text>
  );

const WarningCell: React.FC<{ hasWarning?: boolean; reason?: string | null }> = ({
  hasWarning,
  reason,
}) => {
  if (!hasWarning) return null;
  return (
    <WarningStatusBadge size="lg" showTooltip tooltipContent={reason || 'No reason provided'} />
  );
};

const CopyableCell: React.FC<{ value: string; tooltip?: string; dataCy?: string }> = ({
  value,
  tooltip,
  dataCy,
}) => (
  <Flex className="approval-dashboard__copyable-cell">
    <Text as="span">{value}</Text>
    <CopyButton value={value} tooltip={tooltip} dataCy={dataCy} />
  </Flex>
);

/**
 * A row that is fully stamped by both approvers yet still appears on the pending list
 * means the Stripe transfer threw and the API reverted the record to `pending`. The
 * pending endpoints only ever return status === 'pending', so this is the one shape
 * that signals "approved, but the payment failed".
 *
 * Two escape hatches apply to this state: retry re-fires the transfer, and cancel
 * takes the record off the queue for good (e.g. the creator moved to a different
 * Stripe account, so no number of retries will ever land).
 */
export const isStuckAfterFailedTransfer = (row: {
  status?: string;
  financeApprovedAt?: string | null;
  deptHeadApprovedAt?: string | null;
}): boolean => row.status === 'pending' && !!row.financeApprovedAt && !!row.deptHeadApprovedAt;

const ApprovalDashboard: React.FC = () => {
  const { organizationId } = useParams<{ organizationId: string }>();
  const { currentUser, isSuperadmin } = useCurrentUser();
  const {
    approvals,
    fetchPendingApprovals,
    stampApproval,
    rejectApproval,
    retryApproval,
    bulkStampApprovals,
    bulkRejectApprovals,
  } = useApprovals();
  const {
    manualPayoutApprovals,
    fetchPendingManualPayoutApprovals,
    stampManualPayoutApproval,
    rejectManualPayoutApproval,
    retryManualPayoutApproval,
    bulkStampManualPayoutApprovals,
    bulkRejectManualPayoutApprovals,
  } = useManualPayoutApprovals();
  const { loading: submissionLoading } = useComponentLoading(RESOURCES.SUBMISSION);
  const { loading: payoutLoading } = useComponentLoading(RESOURCES.PAYOUT);

  const isFinanceApprover = currentUser.isFinanceApprover;
  const isDeptHeadApprover = currentUser.isDeptHeadApprover;
  const canStamp = Boolean(isFinanceApprover || isDeptHeadApprover);

  // Per-table row selection state. Keyed by getRowId — submissionId for submissions,
  // approval.id for manual payouts.
  const [submissionSelection, setSubmissionSelection] = useState<RowSelectionState>({});
  const [manualSelection, setManualSelection] = useState<RowSelectionState>({});

  // All hooks must run before any conditional returns
  useEffect(() => {
    fetchPendingApprovals();
    fetchPendingManualPayoutApprovals();
  }, []);

  // If the underlying data refreshes (e.g. after a bulk action) we want to clear
  // selections of rows that no longer exist. Tanstack would otherwise hold onto
  // selected-ness keyed by stale IDs.
  useEffect(() => {
    const validIds = new Set(approvals.map((a) => String(a.submissionId)));
    setSubmissionSelection((prev) => {
      const next: RowSelectionState = {};
      Object.keys(prev).forEach((k) => {
        if (validIds.has(k) && prev[k]) next[k] = true;
      });
      return next;
    });
  }, [approvals]);

  useEffect(() => {
    const validIds = new Set(manualPayoutApprovals.map((a) => String(a.id)));
    setManualSelection((prev) => {
      const next: RowSelectionState = {};
      Object.keys(prev).forEach((k) => {
        if (validIds.has(k) && prev[k]) next[k] = true;
      });
      return next;
    });
  }, [manualPayoutApprovals]);

  if (!isSuperadmin && !canStamp) {
    return (
      <Box className="approval-dashboard">
        <Text variant={TEXT_VARIANTS.H4}>Payout Approvals</Text>
        <Text>You do not have access to this page.</Text>
      </Box>
    );
  }

  const orgId = organizationId ? +organizationId : NaN;

  // Helper: is this row already stamped by the current user's role?
  // Used for the bulk-approve "silently skip" behavior. Reject never skips.
  const isAlreadyStampedByMe = <T extends { financeApprovedAt?: string | null; deptHeadApprovedAt?: string | null }>(
    row: T,
  ): boolean =>
    !!(
      (isFinanceApprover && row.financeApprovedAt) ||
      (isDeptHeadApprover && row.deptHeadApprovedAt)
    );

  // ---- Submission Payouts table -------------------------------------------------
  const submissionColumns = useMemo(() => [
    submissionColumnHelper.accessor(
      (row) =>
        row.organizationName ?? (row.organizationId ? `Org ${row.organizationId}` : '—'),
      {
        id: 'brand',
        header: 'Brand',
        meta: { width: '140px', addTooltip: true, filterType: 'checkbox' },
      },
    ),
    submissionColumnHelper.accessor((row) => row.creatorName ?? `Creator ${row.creatorId}`, {
      id: 'creator',
      header: 'Creator',
      meta: { width: '160px', filterType: 'text' },
    }),
    submissionColumnHelper.accessor('creatorId', {
      header: 'Creator ID',
      cell: (info) => (
        <CopyableCell value={String(info.getValue())} tooltip="Copy creator ID" dataCy="creator-id" />
      ),
      meta: { width: '110px', filterType: 'text' },
    }),
    submissionColumnHelper.accessor((row) => (row.hasWarningStatus ? 'Warning' : ''), {
      id: 'warning',
      header: 'Warning',
      cell: (info) => {
        const row = info.row.original;
        return <WarningCell hasWarning={row.hasWarningStatus} reason={row.warningStatusReason} />;
      },
      meta: { width: '80px', enableSorting: false },
    }),
    submissionColumnHelper.accessor(
      (row) => row.briefName ?? `Brief ${row.briefId}`,
      {
        id: 'brief',
        header: 'Brief',
        cell: (info) => {
          const name = info.getValue();
          const row = info.row.original;
          // Use the brief's own organizationId so cross-brand approvals link to
          // the correct org. Fall back to the URL's orgId for safety.
          const briefOrgId = row.organizationId ?? orgId;
          if (row.accountId && !Number.isNaN(briefOrgId)) {
            const href = LinkCreator.createLink({
              routeKey: 'VIEW_INDIVIDUAL_BRIEF',
              variables: { organizationId: briefOrgId, accountId: row.accountId, briefId: row.briefId },
            });
            return <Link to={href} onClick={(e) => e.stopPropagation()}>{name}</Link>;
          }
          return <>{name}</>;
        },
        meta: { width: '200px', addTooltip: true, filterType: 'text' },
      },
    ),
    submissionColumnHelper.accessor('amountCents', {
      header: 'Amount',
      cell: (info) => {
        const formatted = formatCurrency(info.getValue() / 100);
        return <CopyableCell value={formatted} tooltip="Copy amount" dataCy="amount" />;
      },
      meta: { width: '120px' },
    }),
    submissionColumnHelper.accessor('requestedBy', {
      header: 'Requested By',
      meta: { width: '140px', filterType: 'text' },
    }),
    submissionColumnHelper.accessor('requestedAt', {
      header: 'Requested',
      cell: (info) => formatDate(info.getValue()),
      meta: { width: '120px' },
    }),
    submissionColumnHelper.accessor('financeApprovedAt', {
      header: 'Finance',
      cell: (info) => <Stamp date={info.getValue()} />,
      meta: { width: '120px' },
    }),
    submissionColumnHelper.accessor('deptHeadApprovedAt', {
      // Header label is "Brkfst" per the latest spec — backend field stays as
      // deptHeadApprovedAt (the role-derivation logic still references it).
      header: 'Brkfst',
      cell: (info) => <Stamp date={info.getValue()} />,
      meta: { width: '120px' },
    }),
    ...(canStamp || isSuperadmin
      ? [
          submissionColumnHelper.accessor((row) => row, {
            id: 'actions',
            header: 'Actions',
            enableSorting: false,
            cell: (info) => {
              const approval = info.row.original;
              const alreadyStamped = isAlreadyStampedByMe(approval);
              // A fully-stamped row still on the pending list means the payout failed at
              // Stripe and reverted to pending — superadmins can re-fire it via /execute.
              const stuckAfterFailure = isStuckAfterFailedTransfer(approval);
              const canRetry = isSuperadmin && stuckAfterFailure;
              // Reject is disabled once you have stamped, which leaves a failed payout with
              // no way off the queue. Cancel reuses the same reject endpoint (it accepts any
              // pending record regardless of stamps) for exactly that dead end.
              const canCancel = (isSuperadmin || canStamp) && stuckAfterFailure;
              const tsvRow = [
                approval.creatorId,
                formatCurrency(approval.amountCents / 100),
                approval.briefName ?? '',
              ].join('\t');

              return (
                <Flex className="approval-dashboard__actions">
                  {canStamp && (
                    <>
                      <ActionButton
                        variant={BUTTON_VARIANTS.PRIMARY_SMALL}
                        disabled={!!alreadyStamped}
                        loading={submissionLoading}
                        onClick={(e) => {
                          e.stopPropagation();
                          stampApproval(approval.submissionId);
                        }}
                      >
                        {alreadyStamped ? 'Approved' : 'Approve'}
                      </ActionButton>
                      <PromptButton
                        variant={BUTTON_VARIANTS.SECONDARY_SMALL}
                        disabled={!!alreadyStamped}
                        text="Reject this payout? The submission will need to be re-queued for payment."
                        confirmButtonText="Reject"
                        onConfirm={() => rejectApproval(approval.submissionId)}
                        onClick={(e) => e.stopPropagation()}
                        className="approval-dashboard__reject-btn"
                      >
                        Reject
                      </PromptButton>
                    </>
                  )}
                  {canRetry && (
                    <PromptButton
                      variant={BUTTON_VARIANTS.SECONDARY_SMALL}
                      loading={submissionLoading}
                      text="Before retrying, confirm this payout was not already sent manually in Stripe. Retrying will attempt the transfer again."
                      confirmButtonText="Retry payment"
                      onConfirm={() => retryApproval(approval.submissionId)}
                      onClick={(e) => e.stopPropagation()}
                      className="approval-dashboard__retry-btn"
                    >
                      Retry payment
                    </PromptButton>
                  )}
                  {canCancel && (
                    <PromptButton
                      variant={BUTTON_VARIANTS.SECONDARY_SMALL}
                      loading={submissionLoading}
                      text="Cancel this payout? Confirm in Stripe that no transfer was sent first. The payout is removed from the approval queue and the submission can be re-queued from scratch with Pay Creator."
                      confirmButtonText="Cancel payout"
                      onConfirm={() => rejectApproval(approval.submissionId)}
                      onClick={(e) => e.stopPropagation()}
                      className="approval-dashboard__cancel-btn"
                      dataCy="cancel-payout"
                    >
                      Cancel payout
                    </PromptButton>
                  )}
                  <CopyButton
                    value={tsvRow}
                    tooltip="Copy creator ID, amount, brief (tab-separated)"
                    copiedTooltip="Row copied!"
                    dataCy="copy-row"
                  >
                    Copy row
                  </CopyButton>
                </Flex>
              );
            },
            meta: { width: '220px', enableSorting: false },
          }),
        ]
      : []),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canStamp, isSuperadmin, isFinanceApprover, isDeptHeadApprover, orgId, submissionLoading]);

  // ---- Manual Creator Payouts table ---------------------------------------------
  const manualColumns = useMemo(() => [
    manualColumnHelper.accessor((row) => row.creatorName ?? `Creator ${row.creatorId}`, {
      id: 'creator',
      header: 'Creator',
      cell: (info) => {
        const name = info.getValue();
        const row = info.row.original;
        const href = LinkCreator.createLink({
          routeKey: 'USER_PROFILE',
          variables: { userId: row.creatorId },
        });
        return <Link to={href} onClick={(e) => e.stopPropagation()}>{name}</Link>;
      },
      meta: { width: '160px', filterType: 'text' },
    }),
    manualColumnHelper.accessor('creatorId', {
      header: 'Creator ID',
      cell: (info) => (
        <CopyableCell value={String(info.getValue())} tooltip="Copy creator ID" dataCy="creator-id" />
      ),
      meta: { width: '110px', filterType: 'text' },
    }),
    manualColumnHelper.accessor((row) => (row.hasWarningStatus ? 'Warning' : ''), {
      id: 'warning',
      header: 'Warning',
      cell: (info) => {
        const row = info.row.original;
        return <WarningCell hasWarning={row.hasWarningStatus} reason={row.warningStatusReason} />;
      },
      meta: { width: '80px', enableSorting: false },
    }),
    manualColumnHelper.accessor('amountCents', {
      header: 'Amount',
      cell: (info) => {
        const formatted = formatCurrency(info.getValue() / 100);
        return <CopyableCell value={formatted} tooltip="Copy amount" dataCy="amount" />;
      },
      meta: { width: '120px' },
    }),
    manualColumnHelper.accessor('description', {
      header: 'Description',
      cell: (info) => {
        const description = info.getValue();
        return (
          <CopyableCell value={description} tooltip="Copy description" dataCy="description" />
        );
      },
      meta: { width: '240px', addTooltip: true, filterType: 'text' },
    }),
    manualColumnHelper.accessor('requestedBy', {
      header: 'Requested By',
      meta: { width: '140px', filterType: 'text' },
    }),
    manualColumnHelper.accessor('requestedAt', {
      header: 'Requested',
      cell: (info) => formatDate(info.getValue()),
      meta: { width: '120px' },
    }),
    manualColumnHelper.accessor('financeApprovedAt', {
      header: 'Finance',
      cell: (info) => <Stamp date={info.getValue()} />,
      meta: { width: '120px' },
    }),
    manualColumnHelper.accessor('deptHeadApprovedAt', {
      header: 'Brkfst',
      cell: (info) => <Stamp date={info.getValue()} />,
      meta: { width: '120px' },
    }),
    ...(canStamp || isSuperadmin
      ? [
          manualColumnHelper.accessor((row) => row, {
            id: 'actions',
            header: 'Actions',
            enableSorting: false,
            cell: (info) => {
              const approval = info.row.original;
              const alreadyStamped = isAlreadyStampedByMe(approval);
              const stuckAfterFailure = isStuckAfterFailedTransfer(approval);
              const canRetry = isSuperadmin && stuckAfterFailure;
              const canCancel = (isSuperadmin || canStamp) && stuckAfterFailure;
              const tsvRow = [
                approval.creatorId,
                formatCurrency(approval.amountCents / 100),
                approval.description,
              ].join('\t');

              return (
                <Flex className="approval-dashboard__actions">
                  {canStamp && (
                    <>
                      <ActionButton
                        variant={BUTTON_VARIANTS.PRIMARY_SMALL}
                        disabled={!!alreadyStamped}
                        loading={payoutLoading}
                        onClick={(e) => {
                          e.stopPropagation();
                          stampManualPayoutApproval(approval.id);
                        }}
                      >
                        {alreadyStamped ? 'Approved' : 'Approve'}
                      </ActionButton>
                      <PromptButton
                        variant={BUTTON_VARIANTS.SECONDARY_SMALL}
                        disabled={!!alreadyStamped}
                        text="Reject this manual payout? The brkfst employee will need to submit a new manual payout to retry."
                        confirmButtonText="Reject"
                        onConfirm={() => rejectManualPayoutApproval(approval.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="approval-dashboard__reject-btn"
                      >
                        Reject
                      </PromptButton>
                    </>
                  )}
                  {canRetry && (
                    <PromptButton
                      variant={BUTTON_VARIANTS.SECONDARY_SMALL}
                      loading={payoutLoading}
                      text="Before retrying, confirm this payout was not already sent manually in Stripe. Retrying will attempt the transfer again."
                      confirmButtonText="Retry payment"
                      onConfirm={() => retryManualPayoutApproval(approval.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="approval-dashboard__retry-btn"
                    >
                      Retry payment
                    </PromptButton>
                  )}
                  {canCancel && (
                    <PromptButton
                      variant={BUTTON_VARIANTS.SECONDARY_SMALL}
                      loading={payoutLoading}
                      text="Cancel this payout? Confirm in Stripe that no transfer was sent first. The payout is removed from the approval queue and a new manual payout can be submitted in its place."
                      confirmButtonText="Cancel payout"
                      onConfirm={() => rejectManualPayoutApproval(approval.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="approval-dashboard__cancel-btn"
                      dataCy="cancel-payout"
                    >
                      Cancel payout
                    </PromptButton>
                  )}
                  <CopyButton
                    value={tsvRow}
                    tooltip="Copy creator ID, amount, description (tab-separated)"
                    copiedTooltip="Row copied!"
                    dataCy="copy-row"
                  >
                    Copy row
                  </CopyButton>
                </Flex>
              );
            },
            meta: { width: '220px', enableSorting: false },
          }),
        ]
      : []),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canStamp, isSuperadmin, isFinanceApprover, isDeptHeadApprover, payoutLoading]);

  // ---- Bulk action handlers -----------------------------------------------------

  const selectedSubmissionIds = Object.entries(submissionSelection)
    .filter(([, v]) => v)
    .map(([k]) => Number(k));
  const selectedManualIds = Object.entries(manualSelection)
    .filter(([, v]) => v)
    .map(([k]) => Number(k));

  const handleBulkApproveSubmissions = () => {
    const toStamp: number[] = [];
    let skipped = 0;
    selectedSubmissionIds.forEach((id) => {
      const row = approvals.find((a) => a.submissionId === id);
      if (!row) return;
      if (isAlreadyStampedByMe(row)) {
        skipped += 1;
      } else {
        toStamp.push(id);
      }
    });
    bulkStampApprovals(toStamp, skipped);
    setSubmissionSelection({});
  };

  const handleBulkRejectSubmissions = () => {
    bulkRejectApprovals(selectedSubmissionIds);
    setSubmissionSelection({});
  };

  const handleBulkApproveManual = () => {
    const toStamp: number[] = [];
    let skipped = 0;
    selectedManualIds.forEach((id) => {
      const row = manualPayoutApprovals.find((a) => a.id === id);
      if (!row) return;
      if (isAlreadyStampedByMe(row)) {
        skipped += 1;
      } else {
        toStamp.push(id);
      }
    });
    bulkStampManualPayoutApprovals(toStamp, skipped);
    setManualSelection({});
  };

  const handleBulkRejectManual = () => {
    bulkRejectManualPayoutApprovals(selectedManualIds);
    setManualSelection({});
  };

  const selectAllSubmissions = () => {
    const next: RowSelectionState = {};
    approvals.forEach((a) => {
      next[String(a.submissionId)] = true;
    });
    setSubmissionSelection(next);
  };

  const selectAllManual = () => {
    const next: RowSelectionState = {};
    manualPayoutApprovals.forEach((a) => {
      next[String(a.id)] = true;
    });
    setManualSelection(next);
  };

  return (
    <Box className="approval-dashboard">
      <Text variant={TEXT_VARIANTS.H4} className="approval-dashboard__heading">
        Payout Approvals
      </Text>
      {isSuperadmin && !canStamp && (
        <Text className="approval-dashboard__readonly-note">
          View only — you are not assigned a Finance or Dept Head approver role.
        </Text>
      )}

      <Box className="approval-dashboard__section">
        <Text variant={TEXT_VARIANTS.H5} className="approval-dashboard__section-title">
          Submission Payouts
        </Text>
        <ApprovalToolbar
          canStamp={canStamp}
          selectedCount={selectedSubmissionIds.length}
          totalCount={approvals.length}
          onSelectAll={selectAllSubmissions}
          onSelectNone={() => setSubmissionSelection({})}
          onBulkApprove={handleBulkApproveSubmissions}
          onBulkReject={handleBulkRejectSubmissions}
          rejectPromptText={`Reject ${selectedSubmissionIds.length} submission payout${
            selectedSubmissionIds.length === 1 ? '' : 's'
          }? Each will need to be re-queued from the submission page.`}
          dataCy="approval-dashboard__submission"
        />
        <BrkfstTable
          columns={submissionColumns}
          data={approvals}
          loading={submissionLoading}
          noResultsMessage="No pending submission payouts"
          enableRowSelection={canStamp}
          rowSelection={submissionSelection}
          onRowSelectionChange={setSubmissionSelection}
          getRowId={(row) => String(row.submissionId)}
          enableSorting
          enableColumnFilters
        />
      </Box>

      <Box className="approval-dashboard__section">
        <Text variant={TEXT_VARIANTS.H5} className="approval-dashboard__section-title">
          Manual Creator Payouts
        </Text>
        <ApprovalToolbar
          canStamp={canStamp}
          selectedCount={selectedManualIds.length}
          totalCount={manualPayoutApprovals.length}
          onSelectAll={selectAllManual}
          onSelectNone={() => setManualSelection({})}
          onBulkApprove={handleBulkApproveManual}
          onBulkReject={handleBulkRejectManual}
          rejectPromptText={`Reject ${selectedManualIds.length} manual payout${
            selectedManualIds.length === 1 ? '' : 's'
          }? Each will require a brand new manual payout to retry.`}
          dataCy="approval-dashboard__manual"
        />
        <BrkfstTable
          columns={manualColumns}
          data={manualPayoutApprovals}
          loading={payoutLoading}
          noResultsMessage="No pending manual payouts"
          enableRowSelection={canStamp}
          rowSelection={manualSelection}
          onRowSelectionChange={setManualSelection}
          getRowId={(row) => String(row.id)}
          enableSorting
          enableColumnFilters
        />
      </Box>
    </Box>
  );
};

export default ApprovalDashboard;
